<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps<{
  /** null 表示关闭；approved / rejected 决定文案与校验 */
  decision: 'approved' | 'rejected' | null;
  subject: string;
  saving?: boolean;
}>();
const emit = defineEmits<{ close: []; submit: [comment: string] }>();

const comment = ref('');
const touched = ref(false);

watch(() => props.decision, () => {
  comment.value = '';
  touched.value = false;
});

const isReject = computed(() => props.decision === 'rejected');
/** 退回必须写原因：提交人只能从这句话知道要改什么，写进审计记录 */
const invalid = computed(() => isReject.value && !comment.value.trim());

function submit() {
  touched.value = true;
  if (invalid.value) return;
  emit('submit', comment.value.trim());
}
function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.decision && !props.saving) emit('close');
}
onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <div v-if="decision" class="decision-mask" @click.self="!saving && emit('close')">
    <section class="decision-dialog" role="dialog" aria-modal="true" aria-labelledby="decision-title">
      <header>
        <div>
          <span>{{ isReject ? 'REJECT / RETURN' : 'APPROVE' }}</span>
          <h3 id="decision-title">{{ isReject ? '退回交付物' : '通过交付物' }}</h3>
          <p>{{ subject }}</p>
        </div>
        <button class="inline-button" type="button" :disabled="saving" @click="emit('close')">关闭</button>
      </header>

      <form @submit.prevent="submit">
        <label>
          <span>{{ isReject ? '退回原因 *' : '审批意见（可留空）' }}</span>
          <textarea
            v-model="comment"
            rows="5"
            :placeholder="isReject
              ? '说明哪里不符合要求、需要补什么，提交人会收到这段话'
              : '可记录审批依据、附加条件等，会写入审批记录'"
            @blur="touched = true"
          ></textarea>
          <small v-if="touched && invalid" class="decision-error">
            退回必须填写原因，否则提交人不知道要改什么。
          </small>
          <small v-else class="decision-hint">
            本次决定与意见会写入审批记录并通知提交人。
          </small>
        </label>

        <footer>
          <button
            class="button"
            :class="isReject ? 'button-danger' : 'button-primary'"
            type="submit"
            :disabled="saving"
          >{{ saving ? '提交中…' : isReject ? '确认退回' : '确认通过' }}</button>
          <button class="button" type="button" :disabled="saving" @click="emit('close')">取消</button>
        </footer>
      </form>
    </section>
  </div>
</template>

<style scoped>
.decision-mask { position: fixed; inset: 0; z-index: 60; display: flex; align-items: flex-start; justify-content: center; padding: 6vh 16px; background: rgba(15,23,36,.45); overflow-y: auto; }
.decision-dialog { width: min(600px, 100%); background: var(--white); border: 1px solid var(--line); box-shadow: 0 18px 48px rgba(15,23,36,.22); }
.decision-dialog > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--line); }
.decision-dialog header span { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.decision-dialog header h3 { margin: 3px 0 0; font-size: 18px; }
.decision-dialog header p { margin: 4px 0 0; max-width: 48ch; color: var(--ink); font-size: 13px; line-height: 1.6; }
.decision-dialog form { padding: 16px 20px; }
.decision-dialog label { display: grid; gap: 5px; }
.decision-dialog label > span { font-size: 13px; font-weight: 700; }
.decision-dialog textarea { width: 100%; padding: 8px 10px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 13px; line-height: 1.7; resize: vertical; }
.decision-error { color: var(--risk); font-size: 12px; }
.decision-hint { color: var(--muted); font-size: 12px; }
.decision-dialog footer { display: flex; gap: 8px; margin-top: 16px; }

@media (max-width: 600px) {
  .decision-mask { padding: 0; }
  .decision-dialog { min-height: 100%; }
}
</style>
